import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import Percent from "_shared/percentages/percent";
import _ from "lodash";
import Session from "users/__session/session";
import { AccountType } from "users/account-types/account-type";
import { User } from "users/user";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam,
} from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { HumanReadableProposalStatus, ProposalAction, ProposalStatus } from "work/proposals/__constants/constants";
import ProposalSchema, {
  BaseProposalParams,
  ProposalJSON,
  ProposalParams,
} from "work/proposals/_schemas/proposal-schema";
import { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import ProposalRedlineFactory from "work/proposals/redlining/__factory/proposal-redline-factory";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import { WorkAgreementParams } from "work/work-agreements/_schemas/work-agreement-schema";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import WorkAgreement from "work/work-agreements/work-agreement";
import { Audience } from "./comments/threads/topics/topic-context";
import ProposalRedline from "./redlining/proposal-redline";

export default class Proposal {
  public static get EXAMPLE(): Proposal {
    return new Proposal({
      currentUser: User.EXAMPLE,
      spec: {
        id: Guid.generate(),
        creator: EntityMember.EXAMPLE,
        workAgreement: WorkAgreement.EXAMPLE,
        clientReviewers: [ProposalReviewer.EXAMPLE],
        vendorReviewers: [ProposalReviewer.EXAMPLE],
        negotiable: new AHBoolean(true),
      },
    });
  }

  public static getDefaultSpec(creator: EntityMember): ProposalParams {
    return {
      workAgreement: {
        discount: new Percent(0.03),
      },
      clientReviewers: [],
      vendorReviewers: [],
      negotiable: new AHBoolean(true),
      creator,
    };
  }

  public readonly originalProposal: Proposal;
  public readonly RFPId?: Guid;
  public readonly id?: Guid;
  public readonly creator: EntityMember;
  public readonly createdDate?: AHDate;
  public readonly lastUpdated?: AHDate;
  public readonly workAgreement: WorkAgreement;
  public readonly negotiable: AHBoolean = new AHBoolean(true);
  public readonly responseDueBy?: AHDate;

  public readonly supersededById?: Guid;
  public readonly supersedes?: ProposalParams;
  public readonly latestRevisionId?: Guid;
  public readonly status: ProposalStatus = ProposalStatus.AwaitingSubmission;
  public readonly spec: ProposalParams;
  public readonly availableActions: { [key in ProposalAction]: Guid[] };
  public readonly partyRevision?: ProposalParams;
  public readonly userRevision?: ProposalParams;
  public readonly currentUser: User;
  public readonly redlining?: ProposalRedline;
  public unresolvedFields = new Set<(typeof ProposalFieldName)[keyof typeof ProposalFieldName]>();

  public get baseSpec(): BaseProposalParams {
    return {
      creator: this.spec.creator,
      workAgreement: this.spec.workAgreement,
      clientReviewers: this.spec.clientReviewers,
      vendorReviewers: this.spec.vendorReviewers,
      negotiable: this.spec.negotiable,
    };
  }

  public get workAgreementId(): Guid | undefined {
    return this.workAgreement.id;
  }

  public get name(): ProjectName | undefined {
    return this.spec?.workAgreement?.name ?? undefined;
  }

  public get description(): ProjectDescription | undefined {
    return this.spec?.workAgreement?.description ?? undefined;
  }

  public get clientTeam(): ClientTeam | MaskedTeam | null {
    return this.workAgreement.clientTeam ?? null;
  }
  public get clientReviewersCacheKey(): string {
    const reviewerKeys: string[] = [];
    for (const reviewer of this._clientReviewers) {
      reviewerKeys.push(
        `${reviewer.userId.value}-${reviewer.canEdit}-${reviewer.dateApproved?.toISOString() ?? "not_approved"}`,
      );
    }
    return reviewerKeys.toSorted((a, b) => a.localeCompare(b)).join(",");
  }

  public get clientLeader(): EntityMember | MaskedTeam | null {
    if (Masked.isMasked(this.workAgreement.clientTeam)) {
      return this.workAgreement.clientTeam;
    }
    return this.workAgreement.clientTeam?.leader ?? null;
  }

  public get vendorTeam(): VendorTeam | MaskedTeam | null {
    return this.workAgreement.vendorTeam ?? null;
  }

  public get vendorReviewersCacheKey(): string {
    const reviewerKeys: string[] = [];
    for (const reviewer of this._vendorReviewers) {
      reviewerKeys.push(
        `${reviewer.userId.value}-${reviewer.canEdit}-${reviewer.dateApproved?.toISOString() ?? "not_approved"}`,
      );
    }
    return reviewerKeys.toSorted((a, b) => a.localeCompare(b)).join(",");
  }

  public get teamRestricted(): AHBoolean | MaskedTeam {
    return this.workAgreement.teamRestricted;
  }
  public get vendorLeader(): EntityMember | null | MaskedTeam {
    return this.workAgreement.vendor?.clone() ?? null;
  }

  public get startDate(): AHDate | null | undefined {
    return this.workAgreement.startDate;
  }
  public get endDate(): AHDate | null | undefined {
    return this.workAgreement.endDate;
  }

  public get feeSchedule(): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule {
    if (Masked.isMasked(this.workAgreement.feeSchedule)) {
      return this.workAgreement.feeSchedule;
    }
    return [...this.workAgreement.feeSchedule];
  }

  public get clientPolicies(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.clientPolicies)) {
      return this.workAgreement.clientPolicies;
    }
    return this.workAgreement.clientPolicies.map((doc) => doc.clone());
  }
  public get vendorPolicies(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.vendorPolicies)) {
      return this.workAgreement.vendorPolicies;
    }
    return this.workAgreement.vendorPolicies.map((doc) => doc.clone());
  }
  public get conflicts(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this.workAgreement.conflicts)) {
      return this.workAgreement.conflicts;
    }
    return this.workAgreement.conflicts.map((doc) => doc.clone());
  }
  public get conflictsCheckWaived(): AHBoolean | MaskedContracts {
    return this.workAgreement.conflictsCheckWaived;
  }

  public get discount(): Percent | MaskedDiscount {
    return this.workAgreement?.discount ?? new Percent(0);
  }

  public get additionalVendors(): EntityMember[] {
    return this.workAgreement.additionalVendors.map((vendor) => vendor.clone());
  }

  public get clientTeamTemplateIds(): Guid[] {
    return this.workAgreement.clientTeamTemplateIds;
  }
  public get vendorTeamTemplateIds(): Guid[] {
    return this.workAgreement.vendorTeamTemplateIds;
  }
  public get clientFeeScheduleTemplateIds(): Guid[] {
    return this.workAgreement.clientFeeScheduleTemplateIds;
  }
  public get vendorFeeScheduleTemplateIds(): Guid[] {
    return this.workAgreement.vendorFeeScheduleTemplateIds;
  }

  public get archiveDate(): AHDate | undefined {
    return this.workAgreement.archiveDate;
  }

  public get isHired(): boolean {
    return this.workAgreement.hireDate !== undefined;
  }

  public get hireDate(): AHDate | undefined {
    return this.workAgreement.hireDate;
  }

  public get clientReviewers(): ProposalReviewer[] {
    return [...this._clientReviewers];
  }
  public get vendorReviewers(): ProposalReviewer[] {
    return [...this._vendorReviewers];
  }

  public get associatedUserIds(): Guid[] {
    const userIds: Guid[] = [...this.workAgreement.associatedUserIds];
    if (this.creator) {
      userIds.push(this.creator.userId);
    }
    this._clientReviewers.forEach((reviewer) => {
      userIds.push(reviewer.userId);
    });
    this._vendorReviewers.forEach((reviewer) => {
      userIds.push(reviewer.userId);
    });
    return userIds;
  }

  public get isModifiedByRedline(): boolean {
    return Boolean(this.redlinedRevision && !this.redlinedRevision.isEquivalentTo(this));
  }

  public get displayStatus(): string {
    if (this.status === ProposalStatus.Archived) {
      return HumanReadableProposalStatus.Archived;
    }

    let displayStatus: HumanReadableProposalStatus | string =
      HumanReadableProposalStatus[this.status as keyof typeof HumanReadableProposalStatus] ??
      HumanReadableProposalStatus.Unknown;

    const currentUserId = this.currentUser.id;
    const isClient =
      MaskedTeam.isPresentNotMasked(this.clientLeader) && this.clientLeader.userId.isEqualTo(currentUserId);
    const isTeamLeader =
      MaskedTeam.isPresentNotMasked(this.vendorTeam) && this.vendorTeam.leader?.userId.isEqualTo(currentUserId);
    const isVendor = this.additionalVendors?.some((vendor) => vendor.userId.isEqualTo(currentUserId));

    if (
      (this.status === ProposalStatus.AwaitingApprovalByClient && isClient) ||
      (this.status === ProposalStatus.AwaitingApprovalByTeamLeader && isTeamLeader) ||
      (this.status === ProposalStatus.AwaitingApprovalByVendors && isVendor)
    ) {
      displayStatus = "Awaiting Your Approval";
    }

    if (
      this.clientReviewers.some((reviewer) => currentUserId?.isEqualTo(reviewer.userId) && !reviewer.dateApproved) ||
      this.vendorReviewers.some((reviewer) => currentUserId?.isEqualTo(reviewer.userId) && !reviewer.dateApproved)
    ) {
      displayStatus = "Awaiting Your Review";
    }

    return displayStatus;
  }

  public get isCurrentRevision(): boolean {
    return !this.supersededById && (!this.latestRevisionId || this.latestRevisionId.isEqualTo(this.id));
  }

  constructor(params: {
    currentUser: User;
    spec: ProposalParams;
    options?: {
      withRedline?: boolean;
    };
  }) {
    const { currentUser, spec } = params;
    const withRedline = params.options?.withRedline ?? false;

    this.RFPId = spec.workAgreement.RFPId ?? undefined;
    this.id = spec.id;
    this.creator = spec.creator;
    this.createdDate = spec.createdDate;
    this.lastUpdated = spec.lastUpdated;
    this.negotiable = spec.negotiable ?? new AHBoolean(true);
    this.latestRevisionId = spec.latestRevisionId;
    this.responseDueBy = spec.responseDueBy;

    this.workAgreement = new WorkAgreement(spec.workAgreement);
    this.spec = spec;

    const workAgreementSchema = new WorkAgreementSchema();
    this.proposalSchema = new ProposalSchema(workAgreementSchema, currentUser);

    this.currentUser = currentUser;
    this._clientReviewers = [...spec.clientReviewers];
    this._vendorReviewers = [...spec.vendorReviewers];
    this.supersededById = spec?.supersededById;
    this.status = spec?.status ?? ProposalStatus.AwaitingSubmission;
    this.availableActions = spec.availableActions
      ? {
          [ProposalAction.Submit]: [...spec.availableActions[ProposalAction.Submit]],
          [ProposalAction.Approve]: [...spec.availableActions[ProposalAction.Approve]],
          [ProposalAction.Reject]: [...spec.availableActions[ProposalAction.Reject]],
          [ProposalAction.Revise]: [...spec.availableActions[ProposalAction.Revise]],
          [ProposalAction.Hire]: [...spec.availableActions[ProposalAction.Hire]],
          [ProposalAction.Delete]: [...spec.availableActions[ProposalAction.Delete]],
          [ProposalAction.Cancel]: [...spec.availableActions[ProposalAction.Cancel]],
          [ProposalAction.Edit]: [...spec.availableActions[ProposalAction.Edit]],
          [ProposalAction.Review]: [...spec.availableActions[ProposalAction.Review]],
        }
      : {
          [ProposalAction.Submit]: [],
          [ProposalAction.Approve]: [],
          [ProposalAction.Reject]: [],
          [ProposalAction.Revise]: [],
          [ProposalAction.Hire]: [],
          [ProposalAction.Delete]: [],
          [ProposalAction.Cancel]: [],
          [ProposalAction.Edit]: [],
          [ProposalAction.Review]: [],
        };

    this.supersedes = spec?.supersedes;
    this.partyRevision = spec?.partyRevision;
    this.userRevision = spec?.userRevision;

    const derivedRedline =
      withRedline && (this.isSubmitted || this.supersedes) ? ProposalRedlineFactory.fromProposal(this) : undefined;
    if (spec.existingRedline) {
      this.redlining = this.shouldUseDerivedRedline(spec.existingRedline, derivedRedline)
        ? derivedRedline
        : spec.existingRedline;
    } else if (derivedRedline) {
      this.redlining = derivedRedline;
    }

    this.originalProposal = spec.originalProposal
      ? new Proposal({ spec: spec.originalProposal, currentUser: currentUser })
      : this;
  }

  public get isSubmitted(): boolean {
    return this.status !== ProposalStatus.AwaitingSubmission; // TODO: update this to account for canceled proposals
  }

  public get isApprovedByBothParties(): boolean {
    return [
      ProposalStatus.AwaitingApprovalByTeam,
      ProposalStatus.AwaitingHire,
      ProposalStatus.AwaitingApprovalByVendors,
    ].includes(this.status);
  }

  public get isApprovedByClient(): boolean {
    return [
      ProposalStatus.AwaitingApprovalByTeamLeader,
      ProposalStatus.AwaitingApprovalByTeam,
      ProposalStatus.AwaitingHire,
      ProposalStatus.AwaitingApprovalByVendors,
    ].includes(this.status);
  }

  public get isApprovedByTeamLeader(): boolean {
    return [
      ProposalStatus.AwaitingApprovalByClient,
      ProposalStatus.AwaitingApprovalByTeam,
      ProposalStatus.AwaitingHire,
      ProposalStatus.AwaitingApprovalByVendors,
    ].includes(this.status);
  }

  public get reviewersKey(): string {
    const reviewerKeys: string[] = [];
    for (const reviewer of this._clientReviewers) {
      reviewerKeys.push(`client-${reviewer.userId.value}-${reviewer.canEdit}`);
    }

    for (const reviewer of this._vendorReviewers) {
      reviewerKeys.push(`vendor-${reviewer.userId.value}-${reviewer.canEdit}`);
    }
    return reviewerKeys.toSorted((a, b) => a.localeCompare(b)).join(",");
  }

  public get reviewersAreModified(): boolean {
    return this.clientReviewersAreModified || this.vendorReviewersAreModified;
  }

  public get clientReviewersAreModified(): boolean {
    return (
      this.clientReviewers.length !== this.originalProposal.clientReviewers.length ||
      !this.clientReviewers.every((updatedReviewer) =>
        this.originalProposal.clientReviewers.some((originalReviewer) => originalReviewer.isEqualTo(updatedReviewer)),
      )
    );
  }

  public get vendorReviewersAreModified(): boolean {
    return (
      this.vendorReviewers.length !== this.originalProposal.vendorReviewers.length ||
      !this.vendorReviewers.every((updatedReviewer) =>
        this.originalProposal.vendorReviewers.some((originalReviewer) => originalReviewer.isEqualTo(updatedReviewer)),
      )
    );
  }

  public get isArchived(): boolean {
    return this.status === ProposalStatus.Archived;
  }

  public get redline(): ProposalRedline | undefined {
    return this.redlining;
  }

  public get latestSubmittingParty(): AccountType | undefined {
    if (this.status === ProposalStatus.AwaitingSubmission && this.supersedes) {
      return this.associatedPartyForUserId(this.creator?.userId);
    }

    const revisionCreatorParty = this.revisionCreatorParty;
    if (revisionCreatorParty) {
      return revisionCreatorParty;
    }

    const reviseParty = this.reviseActionParty;
    if (reviseParty) {
      return reviseParty === AccountType.Client ? AccountType.Vendor : AccountType.Client;
    }

    if (this.creatorBelongsToClientParty) return AccountType.Client;
    if (this.creatorBelongsToVendorParty) return AccountType.Vendor;

    return undefined;
  }

  private get revisionCreatorParty(): AccountType | undefined {
    const baselineCreatorParty = this.baselineCreatorParty;

    if (this.partyRevision) {
      const partyRevisionCreatorParty = this.associatedPartyForUserId(this.partyRevision.creator?.userId);
      if (partyRevisionCreatorParty && baselineCreatorParty && partyRevisionCreatorParty !== baselineCreatorParty) {
        return partyRevisionCreatorParty;
      }
    }

    const currentCreatorParty = this.associatedPartyForUserId(this.creator?.userId);
    if (currentCreatorParty && baselineCreatorParty && currentCreatorParty !== baselineCreatorParty) {
      return currentCreatorParty;
    }

    return undefined;
  }

  private get baselineCreatorParty(): AccountType | undefined {
    const baselineCreatorId =
      this.supersedes?.creator?.userId ?? this.spec.originalProposal?.creator?.userId ?? this.creator?.userId;
    return this.associatedPartyForUserId(baselineCreatorId);
  }

  private get reviseActionParty(): AccountType | undefined {
    let clientCanRevise = false;
    let vendorCanRevise = false;

    for (const userId of this.availableActions[ProposalAction.Revise]) {
      if (this.isClientAssociatedUserId(userId)) {
        clientCanRevise = true;
      }
      if (this.isVendorAssociatedUserId(userId)) {
        vendorCanRevise = true;
      }
    }

    if (clientCanRevise === vendorCanRevise) return undefined;
    return clientCanRevise ? AccountType.Client : AccountType.Vendor;
  }

  private get creatorBelongsToClientParty(): boolean {
    return this.isClientAssociatedUserId(this.creator?.userId);
  }

  private get creatorBelongsToVendorParty(): boolean {
    return this.isVendorAssociatedUserId(this.creator?.userId);
  }

  private associatedPartyForUserId(userId?: Guid): AccountType | undefined {
    if (this.isClientAssociatedUserId(userId)) return AccountType.Client;
    if (this.isVendorAssociatedUserId(userId)) return AccountType.Vendor;
    return undefined;
  }

  private isClientAssociatedUserId(userId?: Guid): boolean {
    if (!userId) return false;
    if (Masked.isNotMasked(this.clientLeader) && this.clientLeader?.userId.isEqualTo(userId)) return true;
    return this._clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(userId));
  }

  private isVendorAssociatedUserId(userId?: Guid): boolean {
    if (!userId) return false;
    if (Masked.isMasked(this.vendorTeam)) return false;
    if (this.vendorLeader && Masked.isNotMasked(this.vendorLeader) && this.vendorLeader.userId.isEqualTo(userId)) {
      return true;
    }
    if (this.vendorTeam?.members.some((member) => member.userId.isEqualTo(userId))) {
      return true;
    }
    return this._vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(userId));
  }

  public get isModified(): boolean {
    if (this.hasUnsavedRedlineChanges) {
      return true;
    }
    let reviewersModified = false;
    if (
      Masked.isNotMasked(this.clientLeader) &&
      this.clientLeader &&
      this.currentUser.id?.isEqualTo(this.clientLeader.userId)
    ) {
      reviewersModified =
        this._clientReviewers.length !== this.originalProposal.clientReviewers.length ||
        this._clientReviewers.some(
          (latestReviewer) =>
            !this.originalProposal.clientReviewers.some((originalReviewer) =>
              originalReviewer.isEqualTo(latestReviewer),
            ),
        );
    } else if (
      this.vendorTeam &&
      Masked.isNotMasked(this.vendorTeam) &&
      this.currentUser.id?.isEqualTo(this.vendorTeam.leader?.userId)
    ) {
      reviewersModified =
        this._vendorReviewers.length !== this.originalProposal.vendorReviewers.length ||
        this._vendorReviewers.some(
          (latestReviewer) =>
            !this.originalProposal.vendorReviewers.some((originalReviewer) =>
              originalReviewer.isEqualTo(latestReviewer),
            ),
        );
    }
    return !this.isEquivalentTo(this.originalProposal) || reviewersModified;
  }

  public get redlinedRevision(): Proposal | null {
    if (!this.redlining) return null;
    if (!this.vendorLeader) {
      throw new Error("Cannot create a redlined revision without a vendor team.");
    }
    let currentVendorTeam: VendorTeam | MaskedTeam;
    let currentTeamRestricted: AHBoolean | MaskedTeam;
    const currentTeamMembers = this.redlining.currentTeamMembers;
    if (
      Masked.isMasked(this.vendorLeader) ||
      Masked.isMasked(currentTeamMembers) ||
      Masked.isMasked(this.redlining.teamRestricted)
    ) {
      currentVendorTeam = this.vendorTeam as MaskedTeam;
      currentTeamRestricted = this.teamRestricted;
    } else {
      currentVendorTeam = new VendorTeam({
        leader: this.vendorLeader,
        members: currentTeamMembers,
      });
      currentTeamRestricted = this.redlining.teamRestricted.currentEntry ?? new AHBoolean(false);
    }
    const revisionSpec: ProposalParams = {
      ...this.spec,
      workAgreement: {
        ...this.spec.workAgreement,
        ...this.redlining.currentProjectDates,
        ...this.redlining.currentDocuments,
        name: this.redlining.name.currentEntry ?? undefined,
        description: this.redlining.description.currentEntry ?? undefined,
        feeSchedule: this.redlining.currentFeeSchedule,
        vendorTeam: currentVendorTeam,
        clientTeam: this.clientTeam ?? undefined,
        teamRestricted: currentTeamRestricted,
        discount: this.redlining.currentDiscount,
      },
      responseDueBy: this.redlining.responseDueBy.currentEntry ?? undefined,
    };

    const revision = new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...revisionSpec,
        clientReviewers: this._clientReviewers,
        vendorReviewers: this._vendorReviewers,
        workAgreement: {
          ...revisionSpec.workAgreement,
          clientTeamTemplateIds: this.clientTeamTemplateIds,
          vendorTeamTemplateIds: this.vendorTeamTemplateIds,
          clientFeeScheduleTemplateIds: this.clientFeeScheduleTemplateIds,
          vendorFeeScheduleTemplateIds: this.vendorFeeScheduleTemplateIds,
        },
        originalProposal: this.originalProposal.spec,
      },
      options: {
        withRedline: false,
      },
    });
    return revision;
  }

  public clone(overrides?: Partial<ProposalParams | WorkAgreementParams>): Proposal {
    const clonedProposal = new Proposal({
      spec: {
        clientReviewers: this._clientReviewers.map((r) => r.clone()),
        vendorReviewers: this._vendorReviewers.map((r) => r.clone()),
        negotiable: this.negotiable.clone(),
        existingRedline: this.redlining,
        workAgreement: {
          additionalVendors: this.workAgreement.additionalVendors.map((vendor) => vendor.clone()),
          archiveDate: this.workAgreement.archiveDate?.clone(),
          clientFeeScheduleTemplateIds: this.workAgreement.clientFeeScheduleTemplateIds.map((id) => id.clone()),
          clientPolicies: Masked.isMasked(this.workAgreement.clientPolicies)
            ? this.workAgreement.clientPolicies
            : this.workAgreement.clientPolicies.map((doc) => doc.clone()),
          clientTeamTemplateIds: this.workAgreement.clientTeamTemplateIds.map((id) => id.clone()),
          conflictsCheckWaived: this.workAgreement.conflictsCheckWaived?.clone(),
          conflicts: Masked.isMasked(this.workAgreement.conflicts)
            ? this.workAgreement.conflicts
            : this.workAgreement.conflicts.map((doc) => doc.clone()),
          description: this.workAgreement.description?.clone(),
          discount: this.workAgreement.discount?.clone(),
          endDate: this.workAgreement.endDate?.clone(),
          feeSchedule: Masked.isMasked(this.workAgreement.feeSchedule)
            ? this.workAgreement.feeSchedule
            : this.workAgreement.feeSchedule.map((cat) => (cat as FeeScheduleCategory).clone()),
          clientTeam: Masked.isMasked(this.workAgreement.clientTeam)
            ? this.workAgreement.clientTeam
            : this.workAgreement.clientTeam?.clone(),
          vendorTeam: Masked.isMasked(this.workAgreement.vendorTeam)
            ? this.workAgreement.vendorTeam
            : this.workAgreement.vendorTeam?.clone(),
          hireDate: this.workAgreement.hireDate?.clone(),
          id: this.workAgreement.id?.clone(),
          name: this.workAgreement.name?.clone(),
          RFPId: this.workAgreement.RFPId?.clone(),
          startDate: this.workAgreement.startDate?.clone(),
          teamRestricted: this.workAgreement.teamRestricted?.clone(),
          vendorFeeScheduleTemplateIds: this.workAgreement.vendorFeeScheduleTemplateIds.map((id) => id.clone()),
          vendorPolicies: Masked.isMasked(this.workAgreement.vendorPolicies)
            ? this.workAgreement.vendorPolicies
            : this.workAgreement.vendorPolicies.map((doc) => doc.clone()),
          vendorTeamTemplateIds: this.workAgreement.vendorTeamTemplateIds.map((id) => id.clone()),
          ...overrides,
        },
        availableActions: this.availableActions,
        createdDate: this.createdDate?.clone(),
        creator: this.creator?.clone(),
        id: this.id?.clone(),
        lastUpdated: this.lastUpdated?.clone(),
        partyRevision: this.partyRevision,
        userRevision: this.userRevision,
        supersededById: this.supersededById?.clone(),
        latestRevisionId: this.spec.originalProposal?.latestRevisionId?.clone() ?? this.id?.clone(),
        responseDueBy: this.responseDueBy?.clone(),
        status: this.status,
        supersedes: this.supersedes,
        originalProposal: this.originalProposal.spec,
        ...overrides,
      },
      currentUser: this.currentUser,
      options: {
        withRedline: false,
      },
    });

    clonedProposal.unresolvedFields = new Set(this.unresolvedFields);

    return clonedProposal;
  }

  public hasFeeCategory(category: FeeScheduleCategory): boolean {
    if (Masked.isMasked(this.workAgreement.feeSchedule)) return false;
    return this.workAgreement.feeSchedule.some((cat) => cat.name?.isEqualTo(category.name));
  }

  public isAssociatedWithUserId(id: Guid): boolean {
    if (this.creator?.userId.isEqualTo(id)) return true;
    if (this._clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(id))) return true;
    if (this._vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(id))) return true;
    if (this.status !== ProposalStatus.AwaitingSubmission) {
      if (
        Masked.isPresentNotMasked(this.workAgreement.clientTeam) &&
        this.workAgreement.clientTeam.leader?.userId.isEqualTo(id)
      )
        return true;
      if (Masked.isMasked(this.workAgreement.vendorTeam)) return false;
      if (this.workAgreement.vendorTeam?.leader?.userId.isEqualTo(id)) return true;
      if (this.workAgreement.vendorTeam?.members.some((member) => member.userId.isEqualTo(id))) return true;
    }
    return false;
  }

  public getReviewer(userId?: Guid): ProposalReviewer | undefined {
    if (!userId) return undefined;
    return (
      this._clientReviewers.find((reviewer) => reviewer.userId.isEqualTo(userId)) ||
      this._vendorReviewers.find((reviewer) => reviewer.userId.isEqualTo(userId))
    );
  }

  public isModifiedByBuilder(builder?: ProposalBuilder | null): boolean {
    if (!builder?.currentSpec) return false;

    const currentSpec = builder.currentSpec;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkEquality = (a: any, b: any) => (!a && b) || (a && !a.isEqualTo?.(b));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkArrayEquality = (a: any[], b: any[]) =>
      a.length !== b.length || !a.every((item) => b.some((bItem) => bItem.isEqualTo?.(item)));

    const fieldsToCheck = [
      [currentSpec.workAgreement.teamRestricted, this.teamRestricted],
      [currentSpec.workAgreement.vendorTeam, this.vendorTeam],
      [currentSpec.workAgreement.name, this.name],
      [currentSpec.workAgreement.description, this.description],
      [currentSpec.responseDueBy, this.responseDueBy],
      [currentSpec.workAgreement.startDate, this.startDate],
      [currentSpec.workAgreement.endDate, this.endDate],
      [currentSpec.workAgreement.conflictsCheckWaived, this.conflictsCheckWaived],
      [currentSpec.workAgreement.discount, this.discount],
    ];

    for (const [specField, thisField] of fieldsToCheck) {
      if (checkEquality(specField, thisField)) return true;
    }

    if (
      Masked.isPresentNotMasked(currentSpec.workAgreement.feeSchedule) &&
      Masked.isPresentNotMasked(this.workAgreement.feeSchedule) &&
      (currentSpec.workAgreement.feeSchedule.length !== this.workAgreement.feeSchedule.length ||
        !currentSpec.workAgreement.feeSchedule.every(
          (currentCategory) =>
            Array.isArray(this.feeSchedule) && this.feeSchedule.some((category) => category.isEqualTo(currentCategory)),
        ))
    ) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const arrayChecks: [any[], any[]][] = [
      [currentSpec.workAgreement.clientTeamTemplateIds ?? [], this.clientTeamTemplateIds],
      [currentSpec.workAgreement.vendorTeamTemplateIds ?? [], this.vendorTeamTemplateIds],
      [currentSpec.workAgreement.clientFeeScheduleTemplateIds ?? [], this.clientFeeScheduleTemplateIds],
      [currentSpec.workAgreement.vendorFeeScheduleTemplateIds ?? [], this.vendorFeeScheduleTemplateIds],
      [currentSpec.clientReviewers ?? [], this._clientReviewers],
      [currentSpec.vendorReviewers ?? [], this._vendorReviewers],
    ];

    if (Masked.isNotMasked(currentSpec.workAgreement.clientPolicies) && Masked.isNotMasked(this.clientPolicies)) {
      arrayChecks.push([currentSpec.workAgreement.clientPolicies ?? [], this.clientPolicies]);
    }

    if (Masked.isNotMasked(currentSpec.workAgreement.vendorPolicies) && Masked.isNotMasked(this.vendorPolicies)) {
      arrayChecks.push([currentSpec.workAgreement.vendorPolicies ?? [], this.vendorPolicies]);
    }

    if (Masked.isNotMasked(currentSpec.workAgreement.conflicts) && Masked.isNotMasked(this.conflicts)) {
      arrayChecks.push([currentSpec.workAgreement.conflicts ?? [], this.conflicts]);
    }

    for (const [specArray, thisArray] of arrayChecks) {
      if (checkArrayEquality(specArray, thisArray)) return true;
    }

    return false;
  }

  public toJSON(): ProposalJSON {
    if (!this.id) {
      throw new Error("Cannot serialize a proposal without an id.");
    }
    if (!this.creator) {
      throw new Error("Cannot serialize a proposal without a creator.");
    }
    if (!this.workAgreement) {
      throw new Error("Cannot serialize a proposal without a work agreement.");
    }
    return this.proposalSchema.serialize({
      ...this.spec,
      existingRedline: this.redlining,
    });
  }

  public static fromJSON(params: {
    json?: unknown;
    currentUser: User;
    options?: {
      withRedline?: boolean;
    };
    existingRedline?: ProposalRedline;
  }): Proposal | null | undefined {
    const { json } = params;
    if (isNullOrUndefined(json)) return json;
    const proposalSchema = new ProposalSchema(new WorkAgreementSchema(), params.currentUser);

    return new Proposal({
      ...params,
      spec: proposalSchema.deserialize(json),
    });
  }

  public updateRedline(redline: ProposalRedline): Proposal {
    const updatedProposal = new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...this.spec,
        existingRedline: redline,
        originalProposal: this.originalProposal.spec,
      },
    });

    updatedProposal.unresolvedFields = new Set(this.unresolvedFields);

    return updatedProposal;
  }

  public updateClientTeamTemplateIds(ids: Guid[]): Proposal {
    const newProposal = this.clone({ clientTeamTemplateIds: ids });
    return newProposal;
  }

  public updateVendorTeamTemplateIds(ids: Guid[]): Proposal {
    const newProposal = this.clone({ vendorTeamTemplateIds: ids });
    return newProposal;
  }

  public updateClientFeeScheduleTemplateIds(ids: Guid[]): Proposal {
    const newProposal = this.clone({ clientFeeScheduleTemplateIds: ids });
    return newProposal;
  }

  public updateVendorFeeScheduleTemplateIds(ids: Guid[]): Proposal {
    const newProposal = this.clone({ vendorFeeScheduleTemplateIds: ids });
    return newProposal;
  }

  public updateClientReviewers(reviewers: ProposalReviewer[]): Proposal {
    return this.clone({
      clientReviewers: _.uniqBy(reviewers, (reviewer) => reviewer.userId.value),
    });
  }

  public updateVendorReviewers(reviewers: ProposalReviewer[]): Proposal {
    return this.clone({
      vendorReviewers: _.uniqBy(reviewers, (reviewer) => reviewer.userId.value),
    });
  }

  public getCommentAudienceAndSubscribers(
    isExternal: boolean,
    session: Session,
  ): {
    audience: Audience;
    subscribers: EntityMember[];
  } {
    if (!session.user) throw new Error("Session user is required to get comment audience and subscribers.");
    if (!this.creator) throw new Error("Proposal creator is required to get comment audience.");

    let audience: Audience;
    const subscribers: EntityMember[] = [];

    subscribers.push(session.user.toEntityMember());

    if (isExternal) {
      audience = Audience.AllReviewers;
      subscribers.push(this.creator);

      if (Masked.isPresentNotMasked(this.clientLeader)) subscribers.push(this.clientLeader);

      if (Masked.isPresentNotMasked(this.vendorLeader)) subscribers.push(this.vendorLeader);

      subscribers.push(
        ...this._clientReviewers.map((reviewer) => reviewer.entityMember),
        ...this._vendorReviewers.map((reviewer) => reviewer.entityMember),
      );
    } else if (session.context?.viewingAsVendor) {
      audience = Audience.VendorReviewers;
      if (Masked.isPresentNotMasked(this.vendorLeader)) subscribers.push(this.vendorLeader);
      if (Masked.isPresentNotMasked(this.vendorTeam)) {
        if (this.vendorTeam.leader) subscribers.push(this.vendorTeam.leader);
      }

      subscribers.push(...this._vendorReviewers.map((reviewer) => reviewer.entityMember));
    } else {
      audience = Audience.ClientReviewers;
      if (Masked.isPresentNotMasked(this.clientLeader)) subscribers.push(this.clientLeader);
      subscribers.push(...this._clientReviewers.map((reviewer) => reviewer.entityMember));
    }

    return {
      audience,
      subscribers: _.uniqBy(subscribers, (subscriber) => subscriber.userId.value),
    };
  }

  public isEquivalentTo(other?: Proposal): boolean {
    if (!other) return false;
    if ((this.vendorTeam && !this.vendorTeam?.isEqualTo(other.vendorTeam)) || (!this.vendorTeam && other.vendorTeam))
      return false;
    if (!this.hasEquivalentContracts(other)) return false;
    if (!this.discount.isEqualTo(other.discount)) return false;
    if (!this.teamRestricted.isEqualTo(other.teamRestricted)) return false;

    if (
      Masked.isNotMasked(this.workAgreement.feeSchedule) &&
      Masked.isNotMasked(other.feeSchedule) &&
      (this.workAgreement.feeSchedule?.length !== other.feeSchedule.length ||
        !this.workAgreement.feeSchedule?.every(
          (category, index) => Masked.isNotMasked(other.feeSchedule) && category.isEqualTo(other.feeSchedule[index]),
        ))
    ) {
      return false;
    }

    const namesEqual = (!this.name && !other.name) || this.name?.isEqualTo(other.name);

    const descriptionsEqual =
      (!this.description && !other.description) ||
      (this.description && other.description && this.description.isEqualTo(other.description));

    const clientTeamsEmpty =
      !Masked.isPresentNotMasked(this.workAgreement.clientTeam) &&
      !Masked.isPresentNotMasked(other.workAgreement.clientTeam);

    const clientTeamsEqual = this.workAgreement.clientTeam?.isEqualTo(other.workAgreement.clientTeam);

    const responseDueByEqual =
      (!this.responseDueBy && !other.responseDueBy) || this.responseDueBy?.isEqualTo(other.responseDueBy);

    const startDatesEqual = (!this.startDate && !other.startDate) || this.startDate?.isEqualTo(other.startDate);

    const endDatesEqual = (!this.endDate && !other.endDate) || this.endDate?.isEqualTo(other.endDate);

    const feeScheduleTemplatesEqual =
      this.clientFeeScheduleTemplateIds.length === other.clientFeeScheduleTemplateIds.length &&
      this.clientFeeScheduleTemplateIds.every((thisTemplate) =>
        other.clientFeeScheduleTemplateIds.some((otherTemplate) => thisTemplate.isEqualTo(otherTemplate)),
      ) &&
      this.vendorFeeScheduleTemplateIds.length === other.vendorFeeScheduleTemplateIds.length &&
      this.vendorFeeScheduleTemplateIds.every((thisTemplate) =>
        other.vendorFeeScheduleTemplateIds.some((otherTemplate) => thisTemplate.isEqualTo(otherTemplate)),
      );

    const teamTemplatesEqual =
      this.clientTeamTemplateIds.length === other.clientTeamTemplateIds.length &&
      this.clientTeamTemplateIds.every((thisTemplate) =>
        other.clientTeamTemplateIds.some((otherTemplate) => thisTemplate.isEqualTo(otherTemplate)),
      ) &&
      this.vendorTeamTemplateIds.length === other.vendorTeamTemplateIds.length &&
      this.vendorTeamTemplateIds.every((thisTemplate) =>
        other.vendorTeamTemplateIds.some((otherTemplate) => thisTemplate.isEqualTo(otherTemplate)),
      );

    return Boolean(
      (clientTeamsEmpty || clientTeamsEqual) &&
      namesEqual &&
      descriptionsEqual &&
      this.negotiable.isEqualTo(other.negotiable) &&
      responseDueByEqual &&
      startDatesEqual &&
      endDatesEqual &&
      feeScheduleTemplatesEqual &&
      teamTemplatesEqual,
    );
  }

  public get hasUnsharedRedlineChanges(): boolean {
    if (!this.redlining && this.partyRevision) {
      console.warn("Party revision exists without redlining");
      return false;
    }

    if (!this.redlining?.isResolved) {
      return false;
    }

    if (this.redlining && !this.partyRevision) {
      return this.redlining.hasChanges;
    }

    if (this.redlining?.isResolved && this.lastUpdated?.isAfter(this.partyRevision?.lastUpdated)) {
      return true;
    }

    const schema = new WorkAgreementSchema();
    const currentRedlineSpec = schema.serialize(this.redlinedRevision!.baseSpec.workAgreement);
    const partyRedlineSpec = schema.serialize(this.partyRevision!.workAgreement);
    return !_.isEqualWith(currentRedlineSpec, partyRedlineSpec, (objValue, othValue, key) => {
      if (key === "id") return true; // Don't compare ids
    });
  }

  public get hasUnsavedRedlineChanges(): boolean {
    if (!this.redlining) {
      return false;
    }

    const persistedRedline = this.getPersistedRedline();
    if (!persistedRedline) {
      return false;
    }

    return !this.redlining?.isEqualTo(persistedRedline);
  }

  private readonly _clientReviewers: ProposalReviewer[] = [];
  private readonly _vendorReviewers: ProposalReviewer[] = [];
  private readonly proposalSchema: ProposalSchema;

  private shouldUseDerivedRedline(existingRedline: ProposalRedline, derivedRedline?: ProposalRedline): boolean {
    if (!derivedRedline) return false;
    if (existingRedline.isEqualTo(derivedRedline)) return false;
    if (!this.currentUser.isLatestSubmittingParty(this)) return false;
    const existingRedlineCollapsedToResolvedSnapshot = existingRedline.isResolved && !existingRedline.hasChanges;
    const derivedRedlineStillShowsBlueLines = derivedRedline.hasChanges || !derivedRedline.isResolved;
    const sessionHistoryMatchesDerived = _.isEqual(
      existingRedline.sessionHistory.map((change) => change.toJSON()),
      derivedRedline.sessionHistory.map((change) => change.toJSON()),
    );

    return (
      existingRedlineCollapsedToResolvedSnapshot && derivedRedlineStillShowsBlueLines && sessionHistoryMatchesDerived
    );
  }

  public get sharedPartyBaselineRedline(): ProposalRedline | undefined {
    if (!this.isSubmitted) return this.getPersistedRedline();

    return new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...this.spec,
        existingRedline: undefined,
        userRevision: undefined,
        originalProposal: this.originalProposal.spec,
      },
      options: {
        withRedline: true,
      },
    }).redline;
  }

  private hasEquivalentContracts(other: Proposal): boolean {
    if (!this.conflictsCheckWaived.isEqualTo(other.conflictsCheckWaived)) return false;

    if (
      Masked.isNotMasked(this.workAgreement.conflicts) &&
      Masked.isNotMasked(other.conflicts) &&
      (this.workAgreement.conflicts?.length !== other.conflicts.length ||
        !this.workAgreement.conflicts?.every(
          (category, index) => Masked.isNotMasked(other.conflicts) && category.is(other.conflicts[index]),
        ))
    ) {
      return false;
    }

    if (
      Masked.isNotMasked(this.workAgreement.clientPolicies) &&
      Masked.isNotMasked(other.clientPolicies) &&
      (this.workAgreement.clientPolicies?.length !== other.clientPolicies.length ||
        !this.workAgreement.clientPolicies?.every(
          (category, index) => Masked.isNotMasked(other.clientPolicies) && category.is(other.clientPolicies[index]),
        ))
    ) {
      return false;
    }

    if (
      Masked.isNotMasked(this.workAgreement.vendorPolicies) &&
      Masked.isNotMasked(other.vendorPolicies) &&
      (this.workAgreement.vendorPolicies?.length !== other.vendorPolicies.length ||
        !this.workAgreement.vendorPolicies?.every(
          (category, index) => Masked.isNotMasked(other.vendorPolicies) && category.is(other.vendorPolicies[index]),
        ))
    ) {
      return false;
    }

    return true;
  }

  private getPersistedRedline(): ProposalRedline | undefined {
    if (!this.isSubmitted) return this.redlining;

    return new Proposal({
      currentUser: this.currentUser,
      spec: {
        ...this.spec,
        existingRedline: undefined,
        originalProposal: this.originalProposal.spec,
      },
      options: {
        withRedline: true,
      },
    }).redline;
  }
}
